import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth, isOperator, isPartner, homePathFor } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  /**
   * Which side of the product this subtree belongs to. `operator` covers admins
   * too, `admin` is the back-office superset, `member` is the workspace side and
   * `partner` is the third, narrow one. Omit it for pages every side shares,
   * such as the profile.
   */
  area?: 'operator' | 'admin' | 'member' | 'partner';
}

/** Layout route: renders its nested routes once the caller belongs here. */
export function ProtectedRoute({ area }: ProtectedRouteProps) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const allowed =
    area === undefined
      ? true
      : area === 'admin'
        ? user.role === 'admin'
        : area === 'operator'
          ? isOperator(user)
          : area === 'partner'
            ? isPartner(user)
            : // Named, not "anyone who is not an operator": a role added later
              // must not land in the workspace side by being left out here.
              user.role === 'member';

  if (!allowed) {
    return <Navigate to={homePathFor(user)} replace />;
  }

  return <Outlet />;
}
